import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { saveAs } from "file-saver";

import { ChangeDetectionStrategy, Component, computed, inject, signal } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";

import { CbwService } from "../../../../apis/networkdashboard/rest/services";
import { OverviewService } from "../../../../apis/surfdomeinen/services";
import { SafeButton } from "../../../../components/common/safe-button/safe-button";
import { DialogGenericComponent } from "../../../../components/dialogs/dialog-generic/dialog-generic";
import { slugify } from "../../../../helpers/slugify";
import { AnalyticsService } from "../../../../services/analytics.service";
import { AnalyticsAction, AnalyticsModule } from "../../../../services/analytics.types";
import { AuthService } from "../../../../services/authservice";
import { CollectorCallbackParams } from "./collector-callback";
import { collectorAsn } from "./collectors/asn";
import { CollectorDomain } from "./collectors/domain";
import { collectorLir } from "./collectors/lir";

const ASSETS = [
  { name: "domain", labelKey: "Cert.CBW.CTA.domain" },
  { name: "asn", labelKey: "Cert.CBW.CTA.asn" },
  { name: "lir", labelKey: "Cert.CBW.CTA.lir" },
] as const;
type AssetType = (typeof ASSETS)[number]["name"];

@Component({
  selector: "cbw-tab",
  templateUrl: "./cbw-tab.html",
  styleUrls: ["../../cert.scss", "./cbw-tab.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [SafeButton, TranslatePipe],
})
export class CbwTab {
  private auth = inject(AuthService);
  private cbw = inject(CbwService);
  private dialog = inject(MatDialog);
  private domainsOverviewService = inject(OverviewService);
  private translate = inject(TranslateService);
  private analytics = inject(AnalyticsService);

  protected collectingAsset = signal("");
  protected assets = signal(ASSETS);
  protected isCollectingLir = computed(() => this.collectingAsset() === "lir");
  protected isCollectingAsn = computed(() => this.collectingAsset() === "asn");
  protected isCollectingDomains = computed(() => this.collectingAsset() === "domain");

  protected isLoading = computed(() => this.collectingAsset() !== "");
  protected organisation = computed(() => this.auth.viewingCustomer().name || "unknown");
  protected email = computed(() => this.auth.currentUser()?.email || "unknown");
  protected assetFilename = computed(() => slugify(`${this.organisation()}-${this.collectingAsset()}`));

  private resetState() {
    this.collectingAsset.set("");
  }

  private downloadFile(filename: string, content: string) {
    saveAs(new Blob([content], { type: "text/csv;charset=utf-8" }), filename);
  }

  private runCollection(assetType: AssetType) {
    const customerId = this.auth.viewingCustomer().customerId;
    switch (assetType) {
      case "lir":
        return collectorLir(this.cbw, customerId, this.processCollection.bind(this));
      case "asn":
        return collectorAsn(this.cbw, customerId, this.processCollection.bind(this));
      case "domain":
        return CollectorDomain(this.domainsOverviewService, customerId, this.processCollection.bind(this));
    }
  }

  private processCollection(params: CollectorCallbackParams) {
    if (params.error) {
      this.dialog.open(DialogGenericComponent, {
        data: {
          title: this.translate.instant("Cert.CBW.CollectorError.Title"),
          message: this.translate.instant("Cert.CBW.CollectorError.Body"),
          code: `${params.error.message}`,
        },
      });
      this.resetState();
      return;
    }

    // collection went OK. params.data contains the textdata
    // with each entry on a newline.
    this.analytics.trackFeature(AnalyticsModule.Cert, AnalyticsAction.CbwExport, this.collectingAsset());
    this.downloadFile(`${this.assetFilename()}.csv`, params.data);
    this.resetState();
  }

  protected collect(assetType: AssetType) {
    this.collectingAsset.set(assetType);
    this.runCollection(assetType);
  }
}
